import React, { useState, useEffect, useRef } from 'react';
import Header from './Header'; // adjust the path if needed
import Footer from './Footer'; // adjust the path if needed

// --- HELPER & UTILITY COMPONENTS (These would typically be in a shared components.jsx file) ---

const useOnScreen = (options) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                if (currentRef) {
                    observer.unobserve(currentRef);
                }
            }
        }, options);

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);

    return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
    return (
        <section
            ref={ref}
            id={id}
            className={`${className}  transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
        >
            {children}
        </section>
    );
};

const PageHeader = ({ title, subtitle }) => (
    <section className="relative py-24 bg-slate-50 ">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
                {title}
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
        </div>
    </section>
);

// --- MAIN PAGE COMPONENT ---

const TermsOfServicePage = () => {
    const termsSections = [
        {
            id: '1',
            title: '1. Agreement to Terms',
            content: `By accessing or using Roboman.ai (the "Service"), you agree to be bound by this End User
            License Agreement ("Agreement"). If you do not agree, you must not use the Service. This
            Agreement incorporates the Privacy Policy, Acceptable Use Policy, and any applicable
            Addenda.`
        },
        {
            id: '2',
            title: '2.  Description of the Service',
            content: `The Service provides AI-powered tools for B2B outreach, including email automation,
            LinkedIn messaging assistance, AI-generated proposals, analytics, workflow automation,
            and optional voice-based features. Roboman.ai does not guarantee deliverability,
            responses, revenue, meetings, or business outcomes.`
        },
        {
            id: '3',
            title: '3. Eligibility & Authority',
            content: `You represent that you are at least 18 years old and have authority to bind the business entity on whose behalf you use the Service.`
        },
        {
            id: '4',
            title: '4. License Grant',
            content: `Roboman.ai grants you a limited,non-exclusive,non-transferable,revocable license to access and use the Service solely for your internal business purposes, subject to this Agreement.`
        },
        {
            id: '5',
            title: '5. License Restrictions',
            content: `You must not: (a) resell,sublicense, or commercially exploit the Service; (b) reverse engineer or attempt to extract source code; (c) use the Service for unlawful, deceptive, or abusive outreach; (d) interfere with platform integrity or security.`
        },
        {
            id: '6',
            title: '6. Account Responsibilities',
            content: `You are responsible for safeguarding credentials, all activity under your account, and ensuring that all users comply with this Agreement.`
        },
        {
            id: '7',
            title: '7. AI-Generated Content Disclaimer',
            content: `AI-generated content provided by the Service is supplied on an "as is" basis and may include errors, inaccuracies, or omissions. Roboman.ai makes no guarantee or warranty as to the accuracy, completeness, or suitability of any content generated by its AI. You are solely responsible for reviewing and approving all AI-generated content before using it in your communications or operations. By using such content, you assume all risks and liabilities resulting from its use.`
        },
        {
            id: '8',
            title: '8. Compliance With Laws',
            content: `You are solely responsible for complying with all applicable laws, including UK GDPR, Data Protection Act 2018, PECR, CAN-SPAM, CASL, and third-party platform rules.`
        },
        {
            id: "9",
            title: " 9.Third-Party Platforms",
            content: "The Service may integrate with or assist you in using third-party platforms or services (for example, LinkedIn or email providers) as part of your outreach activities.It is your responsibility to abide by the terms of service and policies of any such third- party platform when using it through Roboman.ai.Roboman.ai is not liable for any consequences arising from your use of third - party platforms via the Service, including any account suspensions,bans, or enforcement actions imposed by those platforms"
        },
        {
            id: "10",
            title: "10. Voice & Identity Usage",
            content: "If you use any voice or audio features of the Service, you warrant that you either own the voice being used or have obtained consent to use it. Impersonation or unauthorized use of another person's voice is strictly prohibited."
        },
        {
            id: "11",
            title: "11. Voice Note Processing & Consent",
            content: "If you choose to use the Service's voice note or audio recording features, you consent to Roboman.ai processing and storing those voice recordings as needed to provide the Service's functionality (e.g., transcribing or transforming audio for campaign delivery). You represent and warrant that you have obtained all necessary permissions or consents from any individuals whose voices are recorded or submitted. Roboman.ai will handle recordings in accordance with its Privacy Policy. You are responsible for ensuring compliance with all relevant privacy laws."
        },
        {
            id: "12",
            title: "12. Fees, Billing & Credits",
            content: "Fees for the Service may be charged through subscription plans or through the purchase of credits. All fees are non-refundable except where required by law. Unused credits have no cash value."
        },
        {
            id: "13",
            title: "13. Suspension & Termination",
            content: "Roboman.ai may suspend or terminate your access to the Service for any violation of this Agreement, any abusive use of the Service, exposure to legal risk, or any harm to the platform. Upon termination, your license immediately ends."
        },
        {
            id: "14",
            title: "14. Data Protection",
            content: "All data processing performed through the Service is governed by Roboman.ai's Privacy Policy and Data Processing Addendum. You act as the data controller and Roboman.ai as the data processor. Both parties agree to comply with applicable data protection laws."
        }
    ];

    return (
        <>
            {/* ✅ Header */}
            <Header />

            <main>
                <PageHeader
                    title="Terms of Service"
                    subtitle="Please read these terms carefully before using our services."
                />

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="bg-white p-8 sm:p-12 rounded-2xl shadow-xl border border-slate-200">
                        <p className="text-sm text-slate-500 mb-8">
                            Last Updated: September 15, 2025
                        </p>
                        <div className="space-y-10">
                            {termsSections.map((section) => (
                                <AnimatedSection key={section.id} id={section.id}>
                                    <h2 className="text-2xl font-bold text-slate-900 mb-4 ">
                                        {section.title}
                                    </h2>
                                    <div className="prose max-w-none text-slate-600 whitespace-pre-line text-justify">
                                        <p>{section.content}</p>
                                    </div>
                                </AnimatedSection>
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            {/* ✅ Footer */}
            <Footer />
        </>
    );
};

export default TermsOfServicePage;
